"use client";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useStoreOptions } from "../../hooks/use-store-options";

/** Chiều cao tối đa của danh sách (khớp class `max-h-60` = 15rem = 240px). */
const MENU_MAX_HEIGHT = 240;
/** Chừa mép màn hình cho đỡ dính sát viền. */
const VIEWPORT_PADDING = 8;

interface Option {
  value: number;
  label: string;
}

interface Props {
  selected: number[];
  onChange: (values: number[]) => void;
  label?: string;
  error?: string;
}

export default function SelectBranchMultiDropdown({
  selected,
  onChange,
  label = "Chi nhánh áp dụng",
  error,
}: Props) {
  const { options: storeOptions, loading } = useStoreOptions();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  // Hướng mở danh sách: 'down' = dưới nút (mặc định), 'up' = lật lên trên khi dưới không đủ chỗ.
  const [placement, setPlacement] = useState<"down" | "up">("down");
  // Chiều cao thực tế cho phép — thu nhỏ khi cả trên lẫn dưới đều chật, để danh sách luôn cuộn được
  // trong vùng nhìn thấy thay vì tràn ra ngoài màn hình.
  const [menuMaxHeight, setMenuMaxHeight] = useState(MENU_MAX_HEIGHT);

  /**
   * Đo chỗ trống quanh nút để quyết định mở lên hay xuống.
   * Component này không dùng thư viện định vị nào — trước đây danh sách luôn nằm dưới nút, nên khi ô
   * ở gần đáy màn hình (vd cuối form tạo nhân viên) thì 240px danh sách bị tràn ra ngoài viewport.
   */
  const updatePlacement = useCallback(() => {
    const el = buttonRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_PADDING;
    const spaceAbove = rect.top - VIEWPORT_PADDING;
    // Ưu tiên mở xuống; chỉ lật lên khi dưới không đủ mà trên thì rộng hơn.
    const openUp = spaceBelow < MENU_MAX_HEIGHT && spaceAbove > spaceBelow;
    setPlacement(openUp ? "up" : "down");
    setMenuMaxHeight(Math.max(120, Math.min(MENU_MAX_HEIGHT, openUp ? spaceAbove : spaceBelow)));
  }, []);

  // Đo TRƯỚC khi trình duyệt vẽ (useLayoutEffect) để không thấy danh sách nhảy vị trí.
  useLayoutEffect(() => {
    if (!isOpen) return;
    updatePlacement();
  }, [isOpen, updatePlacement]);

  // Cuộn/resize khi đang mở → tính lại (modal có thanh cuộn riêng nên bắt cả sự kiện capture).
  useEffect(() => {
    if (!isOpen) return;
    const onViewportChange = () => updatePlacement();
    window.addEventListener("resize", onViewportChange);
    window.addEventListener("scroll", onViewportChange, true);
    return () => {
      window.removeEventListener("resize", onViewportChange);
      window.removeEventListener("scroll", onViewportChange, true);
    };
  }, [isOpen, updatePlacement]);

  // Danh sách hiển thị bao gồm "Tất cả đại lý"
  const combinedOptions: Option[] = [
    { value: -1, label: "Tất cả đại lý" },
    ...storeOptions,
  ];

  // Click ngoài thì đóng dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Toggle chọn option
  const toggleSelect = (value: number) => {
    if (value === -1) {
      // chọn tất cả
      onChange([-1]);
    } else {
      let next: number[] = [];
      if (selected.includes(-1)) {
        // bỏ chọn "tất cả"
        next = [value];
      } else {
        next = selected.includes(value)
          ? selected.filter((v) => v !== value)
          : [...selected, value];
      }
      onChange(next);
    }
  };

  // Text hiển thị trên input
  const displayText =
    selected.includes(-1) || selected.length === storeOptions.length
      ? "Tất cả đại lý"
      : selected
          .map((id) => combinedOptions.find((o) => o.value === id)?.label)
          .filter(Boolean)
          .join(", ") || "Chọn chi nhánh";

  return (
    <div className="relative w-full" ref={ref}>
      <label className="block text-sm font-semibold text-gray-700 mb-1">
        {label} <span className="text-red-500">*</span>
      </label>

      {/* Dropdown input */}
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full rounded-xl border px-3 py-2 text-left flex justify-between items-center"
      >
        <span className="truncate">{loading ? "Đang tải..." : displayText}</span>
        <span className="text-gray-500">{isOpen ? "▲" : "▼"}</span>
      </button>

      {/* Dropdown list */}
      {isOpen && (
        <div
          className={`absolute z-50 w-full bg-white border rounded-xl shadow-lg overflow-y-auto ${
            placement === "up" ? "bottom-full mb-1" : "top-full mt-1"
          }`}
          style={{ maxHeight: menuMaxHeight }}
        >
          {combinedOptions.map((op) => (
            <label
              key={op.value}
              className="flex items-center gap-2 px-3 py-2 hover:bg-blue-50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={
                  selected.includes(-1)
                    ? op.value === -1
                    : selected.includes(op.value)
                }
                onChange={() => toggleSelect(op.value)}
                className="w-4 h-4 accent-blue-600"
              />
              <span>{op.label}</span>
            </label>
          ))}
        </div>
      )}

      {/* Error */}
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}
